import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { HiCode, HiLightningBolt, HiUsers } from 'react-icons/hi';

const capabilities = [
  {
    icon: HiCode,
    title: 'Clean Code',
    description: 'Maintainable and scalable engineering. Code that the next engineer will thank you for.',
    accent: '#00f2ff',
  },
  {
    icon: HiLightningBolt,
    title: 'Fast Delivery',
    description: 'Focused on efficient implementation. Agile cycles, CI/CD pipelines, measurable outcomes.',
    accent: '#a78bfa',
  },
  {
    icon: HiUsers,
    title: 'Collaboration',
    description: 'Strong communication and cross-functional teamwork. Bridge between product and engineering.',
    accent: '#6fe068',
  },
];

const itemVariant = {
  hidden: { opacity: 0, y: 32 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function About() {
  const ref = useRef(null);
  const capRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const capInView = useInView(capRef, { once: true, margin: '-80px' });

  return (
    <section id="about" className="section-padding overflow-hidden" style={{ position: 'relative' }}>
      {/* Subtle section divider glow */}
      <div style={{
        position: 'absolute', top: 0, left: '10%', right: '10%', height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(0,242,255,0.15), transparent)',
      }} />

      <div className="container-custom w-full">
        {/* Main content: two column */}
        <div className="grid lg:grid-cols-2 gap-12 xl:gap-20 items-center mb-24 w-full">
          {/* Left — headline + bio inside glass card like Experience section */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="card p-5 sm:p-8 lg:p-10 w-full max-w-full"
            style={{
              background: 'rgba(13, 20, 29, 0.8)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
            }}
          >
            {/* Section label inside card */}
            <div className="section-label mb-4">
              01 / ABOUT
            </div>

            <h2 className="heading-primary mb-6" style={{ lineHeight: 1.15 }}>
              I build <span className="text-gradient">products</span>,<br className="hidden sm:inline" />{' '}
              not just interfaces.
            </h2>
            <p style={{ color: '#b9cacb', lineHeight: 1.75, fontSize: '1.025rem', marginBottom: '1.25rem' }}>
              I'm a passionate Full Stack Developer with expertise in building modern web applications
              that solve real business problems. With a strong foundation in both frontend and backend
              technologies, I create seamless digital experiences from concept to deployment.
            </p>
            <p style={{ color: '#b9cacb', lineHeight: 1.75, fontSize: '1.025rem', marginBottom: '2rem' }}>
              Currently at{' '}
              <a href="https://www.cognizant.com" target="_blank" rel="noopener noreferrer"
                style={{ color: '#00f2ff', textDecoration: 'none', borderBottom: '1px solid rgba(0,242,255,0.3)' }}>
                Cognizant Technology Solutions
              </a>{' '}
              in Pune, where I specialise in React.js, .NET, cloud-native architectures and increasingly,
              AI/LLM integrations. I'm always chasing the intersection of engineering quality and
              impactful user experiences.
            </p>

            {/* Quick facts */}
            <div className="pt-5 border-t border-white/[0.08]" style={{ display: 'flex', gap: '1.25rem sm:1.5rem', flexWrap: 'wrap' }}>
              {[
                { label: 'Company',    value: 'Cognizant' },
                { label: 'Location',   value: 'Pune, India' },
                { label: 'Focus',      value: 'Full Stack + Cloud + AI' },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div className="text-mono" style={{ color: '#849495', fontSize: '0.65rem', letterSpacing: '0.1em', marginBottom: '0.2rem' }}>
                    {label.toUpperCase()}
                  </div>
                  <div style={{ color: '#dce3f0', fontWeight: 600, fontSize: '0.9rem' }}>{value}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right — portrait as design element */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex items-center justify-center py-6 overflow-hidden sm:overflow-visible"
          >
            {/* Decorative rings */}
            <div className="w-[260px] h-[260px] sm:w-[340px] sm:h-[340px]" style={{
              position: 'absolute',
              borderRadius: '50%',
              border: '1px solid rgba(0,242,255,0.08)',
            }} />
            <div className="w-[200px] h-[200px] sm:w-[280px] sm:h-[280px]" style={{
              position: 'absolute',
              borderRadius: '50%',
              border: '1px solid rgba(0,242,255,0.12)',
            }} />

            {/* Portrait */}
            <div style={{
              width: '220px', height: '220px',
              borderRadius: '50%',
              border: '2px solid rgba(0,242,255,0.25)',
              overflow: 'hidden',
              boxShadow: '0 0 40px rgba(0,242,255,0.1), 0 24px 60px rgba(0,0,0,0.5)',
              position: 'relative',
              zIndex: 2,
            }}>
              <img
                src={require('../img/atman.jpg')}
                alt="Atman Das — Full Stack Developer"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
              />
            </div>

            {/* Accent dot decoration */}
            {[
              { top: '8%', right: '12%' },
              { bottom: '12%', left: '8%' },
              { top: '55%', right: '2%' },
            ].map((pos, i) => (
              <motion.div
                key={i}
                animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2 + i, repeat: Infinity, ease: 'easeInOut', delay: i * 0.7 }}
                style={{
                  position: 'absolute', ...pos,
                  width: 6, height: 6,
                  borderRadius: '50%',
                  background: i === 1 ? '#a78bfa' : '#00f2ff',
                  boxShadow: `0 0 10px ${i === 1 ? '#a78bfa' : '#00f2ff'}`,
                }}
              />
            ))}
          </motion.div>
        </div>

        {/* ─────────────────────────────────────
            CAPABILITIES — Connected horizontal cards
        ───────────────────────────────────── */}
        <div ref={capRef}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={capInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="section-label mb-10"
            style={{ justifyContent: 'flex-start' }}
          >
            CORE CAPABILITIES
          </motion.div>

          <div style={{ position: 'relative' }}>
            {/* Connector line (desktop only) */}
            <div className="hidden lg:block" style={{
              position: 'absolute', top: '50%', left: '16.6%', right: '16.6%',
              height: '1px',
              background: 'linear-gradient(90deg, rgba(0,242,255,0.1), rgba(0,242,255,0.2), rgba(0,242,255,0.1))',
              zIndex: 0,
            }} />

            <div className="grid sm:grid-cols-3 gap-6">
              {capabilities.map((cap, i) => (
                <motion.div
                  key={cap.title}
                  custom={i}
                  variants={itemVariant}
                  initial="hidden"
                  animate={capInView ? 'visible' : 'hidden'}
                  className="card p-8 relative"
                  style={{ zIndex: 1 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = `${cap.accent}30`;
                    e.currentTarget.style.boxShadow = `0 20px 60px rgba(0,0,0,0.5), 0 0 20px ${cap.accent}10`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Number */}
                  <div className="text-mono" style={{ color: cap.accent, opacity: 0.4, fontSize: '0.65rem', letterSpacing: '0.1em', marginBottom: '1rem' }}>
                    0{i + 1}
                  </div>

                  {/* Icon */}
                  <div style={{
                    width: 48, height: 48,
                    borderRadius: '12px',
                    background: `${cap.accent}10`,
                    border: `1px solid ${cap.accent}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: '1.25rem',
                  }}>
                    <cap.icon size={22} color={cap.accent} />
                  </div>

                  <h3 style={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    color: '#dce3f0',
                    marginBottom: '0.6rem',
                    letterSpacing: '-0.01em',
                  }}>
                    {cap.title}
                  </h3>

                  <p style={{ color: '#849495', fontSize: '0.9rem', lineHeight: 1.65 }}>
                    {cap.description}
                  </p>

                  {/* Bottom accent line */}
                  <div style={{
                    position: 'absolute', bottom: 0, left: '10%', right: '10%', height: '2px',
                    background: `linear-gradient(90deg, transparent, ${cap.accent}40, transparent)`,
                    borderRadius: '1px',
                  }} />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section bottom divider */}
      <div style={{
        position: 'absolute', bottom: 0, left: '10%', right: '10%', height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)',
      }} />
    </section>
  );
}
